// src/components/statistiche/MappeView.js

import React, { useEffect, useMemo, useState } from "react";
import {
  MapContainer,
  TileLayer,
  useMap,
  CircleMarker,
  Tooltip,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import "leaflet.heat";

/**
 * Controlla center/zoom della mappa quando cambiano in React.
 */
function MapViewController({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center && Number.isFinite(center[0]) && Number.isFinite(center[1])) {
      map.setView(center, zoom ?? map.getZoom());
    }
  }, [center, zoom, map]);
  return null;
}

/**
 * Calcola un peso 0..1 per un CAP in base alla metrica selezionata.
 * - Densità: appuntamenti / maxApp
 * - Fatturato: fatturato_eur / 30.000 (30k = massimo)
 * - % Chiusura: conversione / maxConv
 */
function computeWeight(p, mode, stats) {
  const app = Number(p.appuntamenti || 0);
  const chiusi = Number(p.contratti_chiusi || 0);
  const fat = Number(p.fatturato_eur || 0);
  const conv = app > 0 ? chiusi / app : 0;

  let w = 0;

  if (mode === "densita") {
    w = stats.maxApp > 0 ? app / stats.maxApp : 0;
  } else if (mode === "fatturato") {
    const target = 30000; // 30k = pieno
    w = target > 0 ? fat / target : 0;
  } else if (mode === "conversione") {
    w = stats.maxConv > 0 ? conv / stats.maxConv : 0;
  }

  return Math.max(0, Math.min(w || 0, 1));
}

/**
 * Layer heatmap che usa Leaflet.heat se disponibile.
 * Usa un pane dedicato 'heatmapPane' con z-index alto.
 */
function HeatLayer({ points, mode, stats }) {
  const map = useMap();

  useEffect(() => {
    if (!map || !points.length) return;

    if (typeof L.heatLayer !== "function") {
      console.warn("[MAPPE] L.heatLayer non disponibile (plugin leaflet.heat).");
      return;
    }

    const paneName = "heatmapPane";
    let pane = map.getPane(paneName);
    if (!pane) {
      map.createPane(paneName);
      pane = map.getPane(paneName);
      if (pane) {
        pane.style.zIndex = "450";
        pane.style.pointerEvents = "none";
      }
    }

    const heatPoints = points
      .map((p) => {
        const lat = Number(p.lat);
        const lng = Number(p.lng);
        if (Number.isNaN(lat) || Number.isNaN(lng)) return null;

        const intensity = computeWeight(p, mode, stats); // 0..1
        return [lat, lng, intensity];
      })
      .filter(Boolean);

    if (!heatPoints.length) return;

    const radius = mode === "fatturato" ? 70 : 45;
    const blur = mode === "fatturato" ? 18 : 30;

    const layer = L.heatLayer(heatPoints, {
      pane: paneName,
      radius,
      blur,
      maxZoom: 17,
      gradient: {
        0.2: "#22c55e",
        0.5: "#f59e0b",
        1.0: "#ef4444",
      },
    });

    layer.addTo(map);
    return () => map.removeLayer(layer);
  }, [map, points, mode, stats]);

  return null;
}

export default function MappeView({ data, loading }) {
  const [mode, setMode] = useState("densita"); // 'densita' | 'fatturato' | 'conversione'
  const [visMode, setVisMode] = useState("blob"); // 'blob' | 'cerchi' | 'entrambi'
  const [selectedCap, setSelectedCap] = useState(null); // CAP in focus
  const [zoneCenter, setZoneCenter] = useState(null); // override centro mappa
  const [zoom, setZoom] = useState(7);

  // 🔧 Soglie configurabili da pannellino
  const [fixMinApp, setFixMinApp] = useState(5);      // app min zona da sistemare
  const [fixMaxConv, setFixMaxConv] = useState(0.3);  // max % chiusura zona da sistemare (0.3 = 30%)
  const [pushMaxApp, setPushMaxApp] = useState(5);    // app max zona da spingere
  const [pushMinConv, setPushMinConv] = useState(0.6);// min % chiusura zona da spingere (0.6 = 60%)

  // Punti grezzi dal backend
  const rawPoints = Array.isArray(data?.mapPoints) ? data.mapPoints : [];

  // Punti effettivi in base alla metrica
  const points = useMemo(() => {
    if (!rawPoints.length) return [];
    if (mode === "densita") return rawPoints;
    return rawPoints.filter((p) => Number(p.contratti_chiusi || 0) > 0);
  }, [rawPoints, mode]);

  // Centro mappa base
  const autoCenter = useMemo(() => {
    if (!points.length) return [45.0, 9.0];
    let latSum = 0;
    let lngSum = 0;
    let count = 0;
    points.forEach((p) => {
      const lat = Number(p.lat);
      const lng = Number(p.lng);
      if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
        latSum += lat;
        lngSum += lng;
        count++;
      }
    });
    if (!count) return [45.0, 9.0];
    return [latSum / count, lngSum / count];
  }, [points]);

  const center = zoneCenter || autoCenter;

  // Statistiche aggregate
  const stats = useMemo(() => {
    if (!points.length) {
      return {
        maxApp: 1,
        maxFat: 1,
        maxConv: 1,
        totFat: 0,
        totContratti: 0,
        totApp: 0,
      };
    }
    let maxApp = 0;
    let maxFat = 0;
    let maxConv = 0;
    let totFat = 0;
    let totContratti = 0;
    let totApp = 0;

    points.forEach((p) => {
      const app = Number(p.appuntamenti || 0);
      const chiusi = Number(p.contratti_chiusi || 0);
      const fat = Number(p.fatturato_eur || 0);
      const conv = app > 0 ? chiusi / app : 0;

      if (app > maxApp) maxApp = app;
      if (fat > maxFat) maxFat = fat;
      if (conv > maxConv) maxConv = conv;

      totFat += fat;
      totContratti += chiusi;
      totApp += app;
    });

    return {
      maxApp: maxApp || 1,
      maxFat: maxFat || 1,
      maxConv: maxConv || 1,
      totFat,
      totContratti,
      totApp,
    };
  }, [points]);

  const renderModeLabel = () => {
    switch (mode) {
      case "densita":
        return "Densità appuntamenti";
      case "fatturato":
        return "Fatturato";
      case "conversione":
        return "Tasso di chiusura";
      default:
        return "";
    }
  };

  const renderModeShort = () => {
    switch (mode) {
      case "densita":
        return "Appuntamenti";
      case "fatturato":
        return "Fatturato";
      case "conversione":
        return "% Chiusura";
      default:
        return "";
    }
  };

  const renderVisLabel = () => {
    switch (visMode) {
      case "blob":
        return "Blob";
      case "cerchi":
        return "Cerchi";
      case "entrambi":
        return "Blob + cerchi";
      default:
        return "";
    }
  };

  // Quick zoom: elenco zone disponibili (da punti)
  const zoneOptions = useMemo(() => {
    const map = new Map();
    points.forEach((p) => {
      const id = p.zona_id || p.zona_label || "?";
      const label = p.zona_label || `Zona ${id}`;
      if (!map.has(id)) {
        map.set(id, { id, label });
      }
    });
    const ORDER = ["Verona", "Modena", "Novara", "Milano Sud", "Milano Nord"];
    return Array.from(map.values()).sort((a, b) => {
      const ia = ORDER.indexOf(a.label);
      const ib = ORDER.indexOf(b.label);
      if (ia === -1 && ib === -1) return a.label.localeCompare(b.label, "it");
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    });
  }, [points]);

  // Zone da spingere / da sistemare in base alle soglie configurabili
  const { zonesToPush, zonesToFix } = useMemo(() => {
    if (!points.length) return { zonesToPush: [], zonesToFix: [] };

    const byZona = new Map();
    points.forEach((p) => {
      const key = p.zona_label || "Zona sconosciuta";
      if (!byZona.has(key)) {
        byZona.set(key, { zona_label: key, app: 0, chiusi: 0, fat: 0 });
      }
      const z = byZona.get(key);
      z.app += Number(p.appuntamenti || 0);
      z.chiusi += Number(p.contratti_chiusi || 0);
      z.fat += Number(p.fatturato_eur || 0);
    });

    const arr = Array.from(byZona.values()).map((z) => {
      const conv = z.app > 0 ? z.chiusi / z.app : 0;
      return { ...z, conv };
    });

    const zonesToPush = arr
      .filter(
        (z) =>
          z.app > 0 &&
          z.app <= pushMaxApp &&
          z.conv >= pushMinConv
      )
      .sort((a, b) => b.conv - a.conv || a.app - b.app)
      .slice(0, 3);

    const zonesToFix = arr
      .filter(
        (z) =>
          z.app >= fixMinApp &&
          z.conv <= fixMaxConv
      )
      .sort((a, b) => b.app - a.app)
      .slice(0, 3);

    return { zonesToPush, zonesToFix };
  }, [points, pushMaxApp, pushMinConv, fixMinApp, fixMaxConv]);

  // CAP selezionato per card "focus"
  const selectedCapData = useMemo(() => {
    if (!selectedCap || !points.length) return null;
    const found = points.find(
      (p) =>
        String(p.cap) === String(selectedCap.cap) &&
        (p.comune || "") === (selectedCap.comune || "")
    );
    if (!found) return null;
    const app = Number(found.appuntamenti || 0);
    const chiusi = Number(found.contratti_chiusi || 0);
    const fat = Number(found.fatturato_eur || 0);
    const conv = app > 0 ? (chiusi / app) * 100 : 0;
    return { ...found, app, chiusi, fat, conv };
  }, [selectedCap, points]);

  // Tabella Top 10 CAP
  const topRows = useMemo(() => {
    if (!points.length) return [];

    const rows = points.map((p) => {
      const app = Number(p.appuntamenti || 0);
      const chiusi = Number(p.contratti_chiusi || 0);
      const fat = Number(p.fatturato_eur || 0);
      const conv = app > 0 ? (chiusi / app) * 100 : 0;

      return {
        cap: p.cap || "—",
        comune: p.comune || "—",
        zona_label: p.zona_label || "",
        appuntamenti: app,
        contratti_chiusi: chiusi,
        fatturato_eur: fat,
        conversione: conv,
      };
    });

    rows.sort((a, b) => {
      if (mode === "densita") return b.appuntamenti - a.appuntamenti;
      if (mode === "fatturato") return b.fatturato_eur - a.fatturato_eur;
      if (mode === "conversione") return b.conversione - a.conversione;
      return b.appuntamenti - a.appuntamenti;
    });

    return rows.slice(0, 10);
  }, [points, mode]);

  // colore cerchi per metrica
  const circleColor = (mode) => {
    if (mode === "fatturato") return "#22c55e"; // verde
    if (mode === "conversione") return "#f97316"; // arancio
    return "#ff4050"; // rosso
  };

  return (
    <div className="stats-section">
      {/* KPIs sopra la mappa */}
      <div className="stats-kpis">
        <div className="kpi-card">
          <div className="kpi-label">Mappe</div>
          <div className="kpi-value">Zone calde / fredde</div>
          <div className="kpi-hint">
            Contratti chiusi, fatturato e densità appuntamenti
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-label">Totale fatturato</div>
          <div className="kpi-value">
            €
            {stats.totFat.toLocaleString("it-IT", {
              maximumFractionDigits: 0,
            })}
          </div>
          <div className="kpi-hint">
            Contratti: {stats.totContratti} · Appuntamenti: {stats.totApp}
          </div>
        </div>
      </div>

            {/* PANNELLO Soglie attenzione */}
      <div className="chart-card" style={{ marginTop: 12, fontSize: 12 }}>
        <div style={{ fontWeight: 600, marginBottom: 8, color: "#e5e7eb" }}>
          Soglie di attenzione
          <span style={{ fontWeight: 400, marginLeft: 6, color: "#9ca3af" }}>
            (usate per "Zone da spingere" e "Zone da sistemare")
          </span>
        </div>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 24,
            alignItems: "flex-start",
          }}
        >
          {/* Zona da sistemare */}
          <div>
            <div style={{ fontWeight: 500, marginBottom: 4 }}>Zona da sistemare</div>
            <div style={{ marginBottom: 4 }}>
              <span>La zona è considerata da sistemare se </span>
              <b>Appuntamenti</b> ≥{" "}
              <input
                type="number"
                min={1}
                value={fixMinApp}
                onChange={(e) =>
                  setFixMinApp(
                    Number.isFinite(Number(e.target.value))
                      ? Number(e.target.value)
                      : 5
                  )
                }
                style={{
                  width: 60,
                  padding: "2px 4px",
                  borderRadius: 6,
                  border: "1px solid #4b5563",
                  background: "#0f172a",
                  color: "#e5e7eb",
                  textAlign: "right",
                  marginRight: 4,
                }}
              />
              <span>e </span>
              <b>% chiusura</b> ≤{" "}
              <input
                type="number"
                min={0}
                max={100}
                value={Math.round(fixMaxConv * 100)}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  const conv = Number.isFinite(v) ? v / 100 : 0.3;
                  setFixMaxConv(Math.max(0, Math.min(conv, 1)));
                }}
                style={{
                  width: 50,
                  padding: "2px 4px",
                  borderRadius: 6,
                  border: "1px solid #4b5563",
                  background: "#0f172a",
                  color: "#e5e7eb",
                  textAlign: "right",
                  marginRight: 2,
                }}
              />
              <span>%</span>
            </div>
            <div style={{ color: "#9ca3af" }}>
              (es.: con i valori sopra, 10 appunti e 20% di chiusura → zona da
              sistemare)
            </div>
          </div>

          {/* Zona da spingere */}
          <div>
            <div style={{ fontWeight: 500, marginBottom: 4 }}>Zona da spingere</div>
            <div style={{ marginBottom: 4 }}>
              <span>La zona è considerata da spingere se </span>
              <b>Appuntamenti</b> ≤{" "}
              <input
                type="number"
                min={1}
                value={pushMaxApp}
                onChange={(e) =>
                  setPushMaxApp(
                    Number.isFinite(Number(e.target.value))
                      ? Number(e.target.value)
                      : 5
                  )
                }
                style={{
                  width: 60,
                  padding: "2px 4px",
                  borderRadius: 6,
                  border: "1px solid #4b5563",
                  background: "#0f172a",
                  color: "#e5e7eb",
                  textAlign: "right",
                  marginRight: 4,
                }}
              />
              <span>e </span>
              <b>% chiusura</b> ≥{" "}
              <input
                type="number"
                min={0}
                max={100}
                value={Math.round(pushMinConv * 100)}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  const conv = Number.isFinite(v) ? v / 100 : 0.6;
                  setPushMinConv(Math.max(0, Math.min(conv, 1)));
                }}
                style={{
                  width: 50,
                  padding: "2px 4px",
                  borderRadius: 6,
                  border: "1px solid #4b5563",
                  background: "#0f172a",
                  color: "#e5e7eb",
                  textAlign: "right",
                  marginRight: 2,
                }}
              />
              <span>%</span>
            </div>
            <div style={{ color: "#9ca3af" }}>
              (es.: 3 appunti e 80% di chiusura → zona da spingere)
            </div>
          </div>
        </div>
      </div>




      {/* CARD suggerimenti zone */}
      <div className="stats-kpis" style={{ marginTop: 8 }}>
        <div className="kpi-card">
          <div className="kpi-label">Zone da spingere</div>
          {zonesToPush.length === 0 ? (
            <div className="kpi-hint">Nessuna zona particolarmente calda.</div>
          ) : (
            <ul style={{ paddingLeft: 16, marginTop: 4, fontSize: 12 }}>
              {zonesToPush.map((z) => (
                <li key={z.zona_label}>
                  <b>{z.zona_label}</b> · {z.app} app ·{" "}
                  {(z.conv * 100).toFixed(1)}% chiusura
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="kpi-card">
          <div className="kpi-label">Zone da sistemare</div>
          {zonesToFix.length === 0 ? (
            <div className="kpi-hint">Nessuna zona critica nel periodo.</div>
          ) : (
            <ul style={{ paddingLeft: 16, marginTop: 4, fontSize: 12 }}>
              {zonesToFix.map((z) => (
                <li key={z.zona_label}>
                  <b>{z.zona_label}</b> · {z.app} app ·{" "}
                  {(z.conv * 100).toFixed(1)}% chiusura
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* CARD MAPPA */}
      <div className="chart-card" style={{ position: "relative", marginTop: 12 }}>
        {/* Toggle metriche + vista + zoom rapido zona */}
        <div
          style={{
            position: "absolute",
            top: 12,
            right: 12,
            zIndex: 1000,
            display: "flex",
            flexDirection: "column",
            gap: 6,
            alignItems: "flex-end",
          }}
        >
          {/* metriche */}
          <div style={{ display: "flex", gap: 4 }}>
            {["densita", "fatturato", "conversione"].map((id) => (
              <button
                key={id}
                type="button"
                onClick={() => setMode(id)}
                style={{
                  padding: "4px 10px",
                  borderRadius: 999,
                  border: "1px solid rgba(148,163,184,.7)",
                  fontSize: 11,
                  fontWeight: 600,
                  background:
                    mode === id ? "rgba(255,64,80,.85)" : "rgba(15,23,42,.75)",
                  color: "#fff",
                  cursor: "pointer",
                  backdropFilter: "blur(6px)",
                }}
              >
                {id === "densita"
                  ? "Densità"
                  : id === "fatturato"
                  ? "Fatturato"
                  : "% Chiusura"}
              </button>
            ))}
          </div>

          {/* vista */}
          <div
            style={{
              display: "flex",
              gap: 4,
              background: "rgba(15,23,42,.85)",
              borderRadius: 999,
              padding: "2px 3px",
              border: "1px solid rgba(148,163,184,.7)",
              backdropFilter: "blur(6px)",
            }}
          >
            {["blob", "cerchi", "entrambi"].map((id) => (
              <button
                key={id}
                type="button"
                onClick={() => setVisMode(id)}
                style={{
                  padding: "2px 8px",
                  borderRadius: 999,
                  border: "none",
                  fontSize: 10,
                  fontWeight: 600,
                  background:
                    visMode === id ? "rgba(248,250,252,.95)" : "transparent",
                  color: visMode === id ? "#0f172a" : "#e5e7eb",
                  cursor: "pointer",
                }}
              >
                {id === "blob" ? "Blob" : id === "cerchi" ? "Cerchi" : "Entrambi"}
              </button>
            ))}
          </div>

          {/* zoom rapido zona */}
          {zoneOptions.length > 0 && (
            <div
              style={{
                display: "flex",
                gap: 4,
                marginTop: 2,
                flexWrap: "wrap",
                justifyContent: "flex-end",
              }}
            >
              {zoneOptions.map((z) => (
                <button
                  key={z.id}
                  type="button"
                  onClick={() => {
                    const subset = points.filter(
                      (p) =>
                        String(p.zona_id) === String(z.id) ||
                        p.zona_label === z.label
                    );
                    if (!subset.length) return;
                    let latSum = 0;
                    let lngSum = 0;
                    let count = 0;
                    subset.forEach((p) => {
                      const lat = Number(p.lat);
                      const lng = Number(p.lng);
                      if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
                        latSum += lat;
                        lngSum += lng;
                        count++;
                      }
                    });
                    if (!count) return;
                    setZoneCenter([latSum / count, lngSum / count]);
                    setZoom(9);
                  }}
                  style={{
                    padding: "2px 8px",
                    borderRadius: 999,
                    border: "1px solid rgba(148,163,184,.4)",
                    fontSize: 10,
                    background: "rgba(15,23,42,.8)",
                    color: "#e5e7eb",
                    cursor: "pointer",
                  }}
                >
                  {z.label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => {
                  setZoneCenter(null);
                  setZoom(7);
                }}
                style={{
                  padding: "2px 8px",
                  borderRadius: 999,
                  border: "1px solid rgba(148,163,184,.4)",
                  fontSize: 10,
                  background: "rgba(248,250,252,.9)",
                  color: "#0f172a",
                  cursor: "pointer",
                }}
              >
                Tutte le zone
              </button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="chart-empty">Caricamento…</div>
        ) : !points.length ? (
          <div className="chart-empty">
            Nessun dato disponibile per i filtri selezionati.
          </div>
        ) : (
          <div
            style={{
              height: 520,
              maxWidth: 1100,
              margin: "0 auto",
              borderRadius: 16,
              overflow: "hidden",
            }}
          >
            <MapContainer
              center={center}
              zoom={zoom}
              style={{ height: "100%", width: "100%" }}
            >
              <MapViewController center={center} zoom={zoom} />

              <TileLayer
                attribution="&copy; OpenStreetMap"
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />

              {/* HEATMAP (Blob / Entrambi) */}
              {(visMode === "blob" || visMode === "entrambi") && (
                <HeatLayer points={points} mode={mode} stats={stats} />
              )}

              {/* CERCHI */}
              {(visMode === "blob" ||
                visMode === "cerchi" ||
                visMode === "entrambi") &&
                points.map((p, idx) => {
                  const lat = Number(p.lat);
                  const lng = Number(p.lng);
                  if (Number.isNaN(lat) || Number.isNaN(lng)) return null;

                  const app = Number(p.appuntamenti || 0);
                  const chiusi = Number(p.contratti_chiusi || 0);
                  const fat = Number(p.fatturato_eur || 0);
                  const conv = app > 0 ? (chiusi / app) * 100 : 0;

                  const w = computeWeight(p, mode, stats);
                  const base = visMode === "blob" ? 6 : 4;
                  const gain = visMode === "blob" ? 16 : 12;
                  const radius = base + gain * w;
                  const color = circleColor(mode);

                  const handleClick = () => {
                    setSelectedCap({ cap: p.cap, comune: p.comune || "" });
                    setZoneCenter([lat, lng]);
                    setZoom(10);
                  };

                  return (
                    <CircleMarker
                      key={`dot-${p.cap || "pt"}-${idx}`}
                      center={[lat, lng]}
                      radius={radius}
                      eventHandlers={{ click: handleClick }}
                      pathOptions={{
                        color,
                        weight: 2,
                        fillColor: color,
                        fillOpacity: visMode === "blob" ? 0.3 : 0.2,
                      }}
                    >
                      <Tooltip direction="top" opacity={0.95}>
                        <div style={{ fontSize: 12 }}>
                          <div>
                            <strong>
                              {p.comune || "Comune sconosciuto"}{" "}
                              {p.cap ? `(${p.cap})` : ""}
                            </strong>
                          </div>
                          {p.zona_label && <div>Zona: {p.zona_label}</div>}
                          <div>Appuntamenti: {app}</div>
                          <div>Contratti chiusi: {chiusi}</div>
                          <div>
                            Fatturato: €
                            {fat.toLocaleString("it-IT", {
                              maximumFractionDigits: 0,
                            })}
                          </div>
                          <div>Conversione: {conv.toFixed(1)}%</div>
                        </div>
                      </Tooltip>
                    </CircleMarker>
                  );
                })}
            </MapContainer>
          </div>
        )}

        {!loading && points.length > 0 && (
          <div
            style={{
              position: "absolute",
              bottom: 12,
              left: 12,
              padding: "6px 10px",
              borderRadius: 999,
              fontSize: 11,
              background: "rgba(15,23,42,.85)",
              color: "#e5e7eb",
              border: "1px solid rgba(148,163,184,.7)",
              backdropFilter: "blur(6px)",
              zIndex: 1001,
            }}
          >
            Vista: <strong>{renderModeLabel()}</strong> ·{" "}
            <span style={{ color: "#ff8a5c" }}>{renderVisLabel()}</span>
          </div>
        )}
      </div>

      {/* CARD FOCUS CAP */}
      {selectedCapData && (
        <div
          className="chart-card"
          style={{
            marginTop: 12,
            display: "flex",
            flexDirection: "column",
            gap: 4,
            fontSize: 13,
          }}
        >
          <div style={{ fontWeight: 600 }}>
            Focus CAP {selectedCapData.cap} – {selectedCapData.comune}
          </div>
          <div style={{ fontSize: 12, color: "#e5e7eb" }}>
            Zona: {selectedCapData.zona_label || "n/d"}
          </div>
          <div style={{ display: "flex", gap: 16, marginTop: 4, fontSize: 12 }}>
            <span>Appuntamenti: {selectedCapData.app}</span>
            <span>Contratti: {selectedCapData.chiusi}</span>
            <span>
              Fatturato: €
              {selectedCapData.fat.toLocaleString("it-IT", {
                maximumFractionDigits: 0,
              })}
            </span>
            <span>Conversione: {selectedCapData.conv.toFixed(1)}%</span>
          </div>
        </div>
      )}

      {/* TABELLINA TOP 10 CAP */}
      {!loading && topRows.length > 0 && (
        <div className="chart-card" style={{ marginTop: 16 }}>
          <div
            style={{
              marginBottom: 8,
              fontSize: 13,
              fontWeight: 600,
              color: "#e5e7eb",
            }}
          >
            Top 10 CAP per{" "}
            <span style={{ color: "#ff8a5c" }}>{renderModeShort()}</span>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table
              className="stats-table top-cap-table"
              style={{
                width: "100%",
                fontSize: 12,
                borderCollapse: "collapse",
                color: "#f9fafb",
              }}
            >
              <thead>
                <tr>
                  <th style={{ textAlign: "left", padding: "6px 8px", background: "rgba(15,23,42,0.9)" }}>
                    CAP
                  </th>
                  <th style={{ textAlign: "left", padding: "6px 8px", background: "rgba(15,23,42,0.9)" }}>
                    Comune
                  </th>
                  <th style={{ textAlign: "right", padding: "6px 8px", background: "rgba(15,23,42,0.9)" }}>
                    Appuntamenti
                  </th>
                  <th style={{ textAlign: "right", padding: "6px 8px", background: "rgba(15,23,42,0.9)" }}>
                    Contratti
                  </th>
                  <th style={{ textAlign: "right", padding: "6px 8px", background: "rgba(15,23,42,0.9)" }}>
                    %
                  </th>
                  <th style={{ textAlign: "right", padding: "6px 8px", background: "rgba(15,23,42,0.9)" }}>
                    Fatturato
                  </th>
                </tr>
              </thead>
              <tbody>
                {topRows.map((r, idx) => (
                  <tr
                    key={`${r.cap}-${idx}`}
                    onClick={() =>
                      setSelectedCap({ cap: r.cap, comune: r.comune })
                    }
                    style={{
                      background:
                        idx % 2 === 0
                          ? "rgba(15,23,42,0.4)"
                          : "rgba(15,23,42,0.25)",
                      cursor: "pointer",
                    }}
                  >
                    <td style={{ padding: "4px 8px" }}>{r.cap}</td>
                    <td style={{ padding: "4px 8px" }}>
                      {r.comune}
                      {r.zona_label ? ` · ${r.zona_label}` : ""}
                    </td>
                    <td style={{ padding: "4px 8px", textAlign: "right" }}>
                      {r.appuntamenti}
                    </td>
                    <td style={{ padding: "4px 8px", textAlign: "right" }}>
                      {r.contratti_chiusi}
                    </td>
                    <td style={{ padding: "4px 8px", textAlign: "right" }}>
                      {r.conversione.toFixed(1)}%
                    </td>
                    <td style={{ padding: "4px 8px", textAlign: "right" }}>
                      €
                      {r.fatturato_eur.toLocaleString("it-IT", {
                        maximumFractionDigits: 0,
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
